import { memo, ReactNode, useContext, useMemo, useRef, useEffect, useState } from 'react';
import { useResultTypeLabel } from 'views/Todo/hooks/useLabel';
import Mustache from 'mustache';
import { Button, Input, Form, InputNumber, Modal, Image, message } from 'antd';
import { css } from '@emotion/css';
import StatusButton from '../../../../components/StatusButton';
import { ArrowDownOutlined, ArrowUpOutlined, EnterOutlined, EyeOutlined } from '@ant-design/icons';
import { useTranslate, TFnType } from '@tolgee/react';
import { useFormikContext, Formik } from 'formik';
import { ReadonlyContext, ToDoBundleContext } from './context';
import { eventBus } from '../../../../services/eventBus';
import useIM from '../../../../hooks/business/im/useIM';
import { Concept } from '../../../../components/TemplateEditor/toolbarPlugins/ConceptMenu';
import { match, when } from 'ts-pattern';
import { useConceptText } from '../../../../components/TemplateEditor/renders/TagConcept';
import FormField from '../../../../components/FormField';
import { useCreation, useEventListener } from 'ahooks';
import { translateTemplate } from '../../../Aide/Scenario/PolicyForm/TranslateTool';
import { getSlateContent } from '../../../Aide/Scenario/PolicyForm';
import { SlateValue2MustacheV2 } from '../../../../components/TemplateEditor/mustacheUtils';
import TranslateMode = Conversation.TranslateMode;
import { getCurrencySymbol } from '../../../../utils/getCurrencySymbol';
import styled from '@emotion/styled';
import { isMac } from 'utils/is';
import { cx } from '@emotion/css';
import { resourceURLToFile } from 'utils/getImage';
import { useSendFileVideo } from '../../Chat/components/MessageEditor/Toolbar/VideoUpload/hooks/useUploadVideo';
import { v4 as uuidv4 } from 'uuid';
import { getVideoMetadata } from 'utils/validator/video';
import { useSendFileImage } from '../../Chat/components/MessageEditor/Toolbar/ImageUpload';
import ProxyVideo from 'components/ProxyVideo';
import posthog from '@leyan/lytics';

interface TemplateNodeProps {
  index: number;
  active: boolean;
  template: Todo.PolicyTemplate;
  showLabel?: boolean;
  submitEffect?: boolean;
  doubleMode?: boolean; //展示原文和翻译
}
const TemplateNode = memo((props: TemplateNodeProps) => {
  const {
    index,
    active,
    template,
    showLabel = true,
    submitEffect = true,
    doubleMode = false,
  } = props;
  const { getResultTypeLabel } = useResultTypeLabel();
  const { t } = useTranslate();
  const { values: pValues, setValues, handleSubmit } = useFormikContext<Todo.TodoInstance>();
  const { currency, tradeAmount } = pValues;
  const readonly = useContext(ReadonlyContext);
  const disabled = pValues?.todoStatus === 3 || readonly;
  const { sendText } = useIM();
  const { conversationId } = useContext(ToDoBundleContext);
  const originMt = useCreation(() => {
    return parseMustache(template.executeContext.content, t);
  }, [template.executeContext.content, t]);
  const mt = useCreation(() => {
    return parseMustache(template.executeContext.translatedContent, t);
  }, [template.executeContext.translatedContent, t]);
  const { getConceptText } = useConceptText();
  const initialValues = useMemo(() => {
    return originMt.concepts.reduce((acc, cur) => {
      acc[cur.id] = '';
      return acc;
    }, {} as Record<string, string>);
  }, [originMt]);
  const initialTouched = useCreation(() => {
    return originMt.concepts.reduce((acc, cur) => {
      acc[cur.id] = true;
      return acc;
    }, {} as Record<string, boolean>);
  }, [originMt]);
  const needValidate = useRef(false);
  const unit = useMemo(() => {
    return getCurrencySymbol(currency);
  }, [currency]);
  const tempResultTypeStatusRef = useRef<Todo.ResultType | undefined>(undefined);
  useEffect(() => {
    const handler = () => {
      if (tempResultTypeStatusRef.current) {
        setValues({
          ...pValues,
          resultTypeStatus: tempResultTypeStatusRef.current,
        });
        handleSubmit();
        tempResultTypeStatusRef.current = undefined;
      }
    };
    eventBus.on('UPDATE_DRAFT_CALLBACK', handler);
    return () => {
      eventBus.off('UPDATE_DRAFT_CALLBACK', handler);
    };
  }, [pValues, setValues, handleSubmit]);

  const inputRef = useRef<any>(null);

  useEffect(() => {
    const input = inputRef.current;

    if (active) {
      input?.focus?.();
    } else if (input) {
      eventBus.emit('MESSAGE_EDITOR_FOCUS');
    }

    return () => {
      if (active && input) {
        eventBus.emit('MESSAGE_EDITOR_FOCUS');
      }
    };
  }, [active]);

  const mtHasValue = useMemo(() => {
    return !!mt.renderString();
  }, [mt]);

  const mtToShow = mtHasValue ? mt : originMt;
  const mtToShowNext = doubleMode ? originMt : undefined;
  const mtToSend = mtToShow;
  const mtToEdit = doubleMode ? mtToShowNext || mtToShow : mtToShow;
  const sendFileImage = useSendFileImage();
  const sendFileVideo = useSendFileVideo();

  return (
    <Formik
      enableReinitialize
      initialTouched={initialTouched}
      initialValues={initialValues}
      onSubmit={() => {}}
      validateOnBlur
      validateOnChange
    >
      {({ validateForm, values }) => {
        const addonUnitValues = {
          ...values,
          退款金额: values['退款金额'] ? `${unit} ${values['退款金额']}` : '',
          补偿金额: values['补偿金额'] ? `${unit} ${values['补偿金额']}` : '',
        };
        const hotkey = isMac ? 'cmd' : 'alt';
        const send = () => {
          needValidate.current = true;
          return validateForm().then((value) => {
            if (Object.keys(value).length > 0) {
              inputRef.current?.focus?.();
              return Promise.reject();
            }
            const text = mtToSend.renderString(addonUnitValues);
            const images = mtToSend.images;
            const videos = mtToSend.videos;
            images.forEach(async (url) => {
              const file = await resourceURLToFile(url);
              sendFileImage(file);
            });

            videos.forEach(async (url) => {
              const file = await resourceURLToFile(url);
              getVideoMetadata(url).then(({ width, height }) => {
                const uuid = uuidv4();
                sendFileVideo(file, width, height, uuid);
              });
            });

            return sendText(conversationId, text, undefined).finally(() => {
              eventBus.emit('ON_SEND_MESSAGE');
              if (submitEffect) {
                setValues({
                  ...pValues,
                  resultTypeStatus: template.resultType,
                });
                handleSubmit();
              }
            });
          });
        };
        const edit = () => {
          needValidate.current = false;
          validateForm().then(() => {
            eventBus.emit('UPDATE_DRAFT', mtToEdit.renderString(addonUnitValues));
            if (mtToEdit.images.length > 0 || mtToEdit.videos.length > 0) {
              message.warn(t('暂不支持编辑图片/视频，请直接查看后点击发送按钮'));
            }
            if (submitEffect) {
              tempResultTypeStatusRef.current = template.resultType;
            }
          });
        };
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEventListener('keydown', (e) => {
          if (active) {
            const { key, altKey, metaKey } = e;
            if (isMac ? metaKey : altKey) {
              if (key === '\\') {
                edit();
                e.preventDefault();
                posthog.capture('$hotKey', {
                  hotKey: `${isMac ? 'Cmd' : 'Alt'}+\\`,
                  todoId: values.id,
                });
              } else if (key === 'Enter') {
                send();
                e.preventDefault();
                posthog.capture('$hotKey', {
                  hotKey: `${isMac ? 'Cmd' : 'Alt'}+Enter`,
                  todoId: values.id,
                });
              }
            }
          }
        });
        return (
          <Form
            layout="vertical"
            className={css`
              .ant-form-item {
                margin-bottom: 0 !important;
                flex-grow: 1;
              }
            `}
          >
            <div className="mb-[12px] last:mb-0">
              <div>
                {showLabel && (
                  <div className="flex border-0 border-b border-solid border-[#f0f2f5] px-[16px] items-center py-[7px] leading-[18px] text-[12px] text-[#232426] justify-between">
                    <div>{getResultTypeLabel(template.resultType)}</div>
                    <div style={{ color: '#84868C' }}>
                      {hotkey}+{index + 1}
                    </div>
                  </div>
                )}
                <div className="px-[16px] py-[7px] leading-[20px] text-[#515359] whitespace-pre-wrap">
                  <div className={cx(doubleMode ? 'line-clamp-3' : '')}>
                    {mtToShow.renderTemplate(addonUnitValues)}
                  </div>
                  <div className="mt-[4px] flex flex-wrap gap-[8px]">
                    {mtToShow.images.map((url) => {
                      return <CopyMedia key={url} type="image" url={url} />;
                    })}
                    {mtToShow.videos.map((url) => {
                      return <CopyMedia key={url} type="video" url={url} />;
                    })}
                  </div>
                  {mtToShowNext && mtToShowNext !== mtToShow ? (
                    <div className="border-0 border-t border-dashed border-[#e5e5e5] mt-[6px] pt-[6px]">
                      {mtToShowNext.renderTemplate(addonUnitValues)}
                    </div>
                  ) : null}
                </div>
                {originMt.concepts.map((c) => {
                  return (
                    <div
                      className="px-[16px] py-[7px] border-0 border-t border-solid border-[#f0f2f5]"
                      key={c.id}
                    >
                      {match(c)
                        .with(
                          {
                            id: when((t) =>
                              [
                                '物流单号',
                                '使用方法',
                                '优惠券面值',
                                '优惠券时限',
                                '补偿方式',
                                '发货时间',
                                '取消原因',
                                '拒绝原因',
                              ].includes(t),
                            ),
                          },
                          (i) => {
                            return (
                              <div className="flex flex-col items-start">
                                <div className="flex-shrink-0 mr-[4px] leading-[34px]">
                                  {getConceptText(i.label)}:
                                </div>
                                <FormField
                                  name={i.id}
                                  validate={(value) => {
                                    if (!value && needValidate.current) {
                                      return t('department.请输入x', {
                                        x: getConceptText(i.label),
                                      });
                                    }
                                  }}
                                >
                                  {({ field }) => {
                                    return (
                                      <Input
                                        ref={inputRef}
                                        className="flex-grow"
                                        placeholder={t('department.请输入x', {
                                          x: getConceptText(i.label),
                                        })}
                                        {...field}
                                      />
                                    );
                                  }}
                                </FormField>
                              </div>
                            );
                          },
                        )
                        .with({ id: when((t) => ['退款金额', '补偿金额'].includes(t)) }, (i) => {
                          return (
                            <>
                              <div className="flex items-center gap-1">
                                <div className="flex-shrink-0">{getConceptText(i.label)}:</div>
                                <FormField
                                  name={i.id}
                                  validate={(value) => {
                                    if (!value && needValidate.current) {
                                      return t('department.请输入x', {
                                        x: getConceptText(i.label),
                                      });
                                    }
                                  }}
                                >
                                  {({ field }) => {
                                    return (
                                      <div className="!w-full flex items-center">
                                        <InputNumber
                                          ref={inputRef}
                                          className="flex-grow"
                                          placeholder={t('department.请输入x', {
                                            x: getConceptText(i.label),
                                          })}
                                          addonBefore={unit}
                                          max={Number(tradeAmount) || undefined}
                                          {...field}
                                        />
                                      </div>
                                    );
                                  }}
                                </FormField>
                              </div>
                              {tradeAmount && (
                                <div className="mt-2" title="3">
                                  {t('translation.订单金额')}: {unit} {tradeAmount}
                                </div>
                              )}
                            </>
                          );
                        })
                        .otherwise(() => null)}
                    </div>
                  );
                })}
              </div>
              {!disabled && (
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <StatusButton type="link" onClick={send}>
                    {t('发送')}
                  </StatusButton>
                  <Button type="link" onClick={edit} disabled={!mtToEdit.renderString()}>
                    {t('编辑')}
                  </Button>
                </div>
              )}
            </div>
          </Form>
        );
      }}
    </Formik>
  );
});

type MT = {
  renderString: (data?: Record<string, string>) => string;
  renderTemplate: (data?: Record<string, string>) => ReactNode;
  concepts: Concept[];
  isEmpty: () => boolean;
  videos: string[];
  images: string[];
};

export const parseMustache = (mustache: string, t: TFnType): MT => {
  try {
    mustache = mustache.replace('#mustache#', '');
    const concepts: Concept[] = [];
    const images: string[] = [];
    const videos: string[] = [];
    Mustache.render(mustache, {
      concept: () => {
        return (text: string) => {
          const c = decodeURIComponent(text) as Todo.Concept;
          concepts.push({
            id: c,
            label: t(c),
          });
          return `<span class="text-[#3366ff]">@${t(decodeURIComponent(text))}</span>`;
        };
      },
      media: () => {
        return (text: string) => {
          try {
            const meta = JSON.parse(decodeURIComponent(text));
            if (meta['mediaType'] === 'MEDIA_VIDEO' && meta['localUrl']) {
              videos.push(meta['localUrl']);
            }
            if (meta['mediaType'] === 'MEDIA_IMAGE' && meta['localUrl']) {
              images.push(meta['localUrl']);
            }
          } catch (err) {}
        };
      },
    });
    const renderTemplate = (data?: Record<string, string>) => {
      const html = Mustache.render(mustache, {
        text: () => {
          return (text: string) => {
            return decodeURIComponent(text);
          };
        },
        concept: () => {
          return (text: string) => {
            const c = decodeURIComponent(text) as Todo.Concept;
            if (data?.[c]) {
              return data[c];
            } else {
              return `<span class="text-[#3366ff]">@${t(decodeURIComponent(text))}</span>`;
            }
          };
        },
      });
      return (
        <span
          dangerouslySetInnerHTML={{
            __html: html,
          }}
        ></span>
      );
    };
    const renderString = (data?: Record<string, string>) => {
      return Mustache.render(mustache, {
        text: () => {
          return (text: string) => {
            return decodeURIComponent(text);
          };
        },
        concept: () => {
          return (text: string) => {
            const c = decodeURIComponent(text) as Todo.Concept;
            return data?.[c] || `@${t(decodeURIComponent(text))}`;
          };
        },
      });
    };
    return {
      renderString,
      renderTemplate,
      concepts,
      isEmpty: () => {
        return !renderString() && images.length === 0 && videos.length === 0;
      },
      images,
      videos,
    };
  } catch (err) {
    return {} as MT;
  }
};

TemplateNode.displayName = 'TemplateNode';
export default TemplateNode;

export const translateMustache = (
  mustache: string,
  lang: General.Language,
  mode: TranslateMode,
) => {
  return translateTemplate(getSlateContent(mustache), lang, [], mode, { from: 'todoBundle' }).then(
    (result) => {
      return `#mustache#${SlateValue2MustacheV2(result)}`;
    },
  );
};

export function HotkeyTips() {
  const { t } = useTranslate();
  const hotkey = isMac ? 'cmd' : 'alt';
  return (
    <HotkeyTipsContainer>
      <span>
        {hotkey}+
        <ArrowUpOutlined /> {hotkey}+
        <ArrowDownOutlined /> {t('phrase.切换')}
      </span>
      <span>
        {hotkey}+
        <EnterOutlined /> {t('发送')}
      </span>
      <span>
        {hotkey}+\ {t('编辑')}
      </span>
    </HotkeyTipsContainer>
  );
}

const HotkeyTipsContainer = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #84868c;
  margin-bottom: 12px;

  > span {
    display: flex;
    align-items: center;
    margin-right: 12px;
    .anticon {
      margin-top: -3px;
    }
  }
`;

type CopyMediaProps = {
  type: 'image' | 'video';
  url: string;
};
export const CopyMedia = (props: CopyMediaProps) => {
  const { type, url } = props;
  const [visible, setVisible] = useState(false);
  const { t } = useTranslate();
  const sendFileVideo = useSendFileVideo();
  const sendFileImage = useSendFileImage();

  return (
    <div
      className="w-[32px] h-[32px] bg-[#bbb] relative group cursor-pointer"
      onClick={() => setVisible(true)}
    >
      <div className="w-full h-full bg-[#01010126] absolute left-0 right-0 top-0 bottom-0 hidden group-hover:!flex items-center justify-center z-[12]">
        <EyeOutlined className="!text-[#fff]" />
      </div>
      {type === 'image' ? (
        <img className="w-full h-full object-cover" src={url} alt="" />
      ) : (
        <ProxyVideo className="h-full w-full" src={url} controls={false} />
      )}
      <Modal
        className="[&_.ant-modal-body]:pt-[60px]"
        visible={visible}
        footer={
          <div className="flex justify-end items-center">
            <Button
              type="primary"
              onClick={async () => {
                if (type === 'video') {
                  const file = await resourceURLToFile(url);
                  getVideoMetadata(url).then(({ width, height }) => {
                    const uuid = uuidv4();

                    sendFileVideo(file, width, height, uuid);
                  });
                } else {
                  const file = await resourceURLToFile(url);
                  sendFileImage(file);
                }
                setVisible(false);
              }}
            >
              {t('发送')}
            </Button>
          </div>
        }
        onCancel={(e) => {
          setVisible(false);
          e.stopPropagation();
        }}
      >
        <div className="flex justify-center items-center">
          {type === 'image' ? (
            <Image className="max-h-[350px] max-w-[460px] object-cover" preview src={url} alt="" />
          ) : (
            <ProxyVideo className="max-h-[350px] max-w-[460px]" src={url} controls={true} />
          )}
        </div>
      </Modal>
    </div>
  );
};

CopyMedia.displayName = 'CopyMedia';
